'use client';

import { useMemo } from 'react';
import { Box } from '@terra/ui-styled-system';
import { ArrowDown01Icon, Tick01Icon } from '@terra/ui-icons';
import { Field } from '../../../components/field';
import { Select, createSelectListCollection } from '../../../components/select';
import { SettingsEditDialog } from '../../settings/SettingsEditDialog';
import { useDraftOnOpen } from '../../application-general/useDraftOnOpen';

export type InternalNetworkExecutionSelectDialogProps<TValue extends string> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  label: string;
  helperText?: string;
  value: TValue;
  items: Array<{ label: string; value: TValue }>;
  isValue: (value: string) => value is TValue;
  isSubmitBlocked: boolean;
  isUpdating: boolean;
  onSubmit: (value: TValue) => Promise<boolean>;
};

export function InternalNetworkExecutionSelectDialog<TValue extends string>({
  open,
  onOpenChange,
  title,
  label,
  helperText,
  value,
  items,
  isValue,
  isSubmitBlocked,
  isUpdating,
  onSubmit,
}: InternalNetworkExecutionSelectDialogProps<TValue>) {
  const [draft, setDraft] = useDraftOnOpen({ open, value });
  const collection = useMemo(
    () => createSelectListCollection({ items }),
    [items],
  );

  return (
    <SettingsEditDialog
      open={open}
      title={title}
      onOpenChange={onOpenChange}
      onSubmit={() => {
        if (isSubmitBlocked) {
          return;
        }

        void onSubmit(draft).then((ok) => ok && onOpenChange(false));
      }}
      loading={isUpdating}
    >
      <Field.Root>
        <Field.Label>{label}</Field.Label>
        {helperText ? <Field.HelperText>{helperText}</Field.HelperText> : null}
        <Box w="full">
          <Select.Root
            collection={collection}
            positioning={{ sameWidth: true }}
            value={[draft]}
            onValueChange={(details) => {
              const next = details.value[0];

              if (next && isValue(next)) {
                setDraft(next);
              }
            }}
            size="sm"
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText />
                <ArrowDown01Icon size={14} />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                <Select.ItemGroup>
                  {collection.items.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      <Select.ItemText>{item.label}</Select.ItemText>
                      <Select.ItemIndicator>
                        <Tick01Icon size={16} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.ItemGroup>
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Box>
      </Field.Root>
    </SettingsEditDialog>
  );
}
